import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Briefcase, MapPin, Clock, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { careerPositions } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CareersPage = () => {
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    portfolio: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/career-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          position: selectedPosition.title
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Candidatura enviada com sucesso! Entraremos em contato em breve.', {
          duration: 4000,
        });
        setFormData({ name: '', email: '', phone: '', linkedin: '', portfolio: '', message: '' });
        setSelectedPosition(null);
      } else {
        toast.error(data.detail || 'Erro ao enviar candidatura. Tente novamente.', {
          duration: 4000,
        });
      }
    } catch (error) {
      toast.error('Erro de conexão. Tente novamente mais tarde.', {
        duration: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-dark-primary py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-purple-light hover:text-purple-primary transition-colors mb-8">
          <ArrowLeft className="mr-2" size={20} />
          Voltar para Home
        </Link>

        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold font-archive text-white mb-4 glow-text">
            Trabalhe Conosco
          </h1>
          <p className="text-xl text-gray-400 font-hero max-w-2xl mx-auto">
            Faça parte da equipe que está revolucionando o gaming competitivo
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Available Positions */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold font-archive text-purple-light mb-6">
              Vagas Abertas
            </h2>
            {careerPositions.map((position) => (
              <Card
                key={position.id}
                className={`bg-dark-secondary border transition-all duration-300 cursor-pointer ${
                  selectedPosition?.id === position.id
                    ? 'border-purple-primary shadow-lg shadow-purple-primary/20'
                    : 'border-purple-primary/20 hover:border-purple-light/50'
                }`}
                onClick={() => setSelectedPosition(position)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl font-archive text-white mb-2">
                        {position.title}
                      </CardTitle>
                      <CardDescription className="text-gray-400 font-hero flex items-center gap-2">
                        <Briefcase size={16} />
                        {position.department}
                      </CardDescription>
                    </div>
                    <Badge className="bg-green-primary text-white">Aberta</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {position.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {position.type}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Application Form */}
          <div>
            <Card className="bg-dark-secondary border-purple-primary/30 sticky top-24">
              <CardHeader>
                <CardTitle className="text-2xl font-archive text-white">
                  {selectedPosition ? `Candidatar-se para ${selectedPosition.title}` : 'Envie sua Candidatura'}
                </CardTitle>
                <CardDescription className="text-gray-400 font-hero">
                  {selectedPosition 
                    ? 'Preencha o formulário abaixo com seus dados'
                    : 'Selecione uma vaga ao lado para começar'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedPosition ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="career-name" className="text-white font-hero">Nome Completo</Label>
                      <Input
                        id="career-name"
                        name="name"
                        type="text"
                        placeholder="Seu nome"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="career-email" className="text-white font-hero">E-mail</Label>
                      <Input
                        id="career-email"
                        name="email"
                        type="email"
                        placeholder="seu@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="career-phone" className="text-white font-hero">Telefone</Label>
                      <Input
                        id="career-phone"
                        name="phone"
                        type="tel"
                        placeholder="+55 (11) 99999-9999"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="career-linkedin" className="text-white font-hero">LinkedIn</Label>
                      <Input
                        id="career-linkedin"
                        name="linkedin"
                        type="url"
                        placeholder="https://linkedin.com/in/seu-perfil"
                        value={formData.linkedin}
                        onChange={handleChange}
                        className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="career-portfolio" className="text-white font-hero">Portfolio / GitHub</Label>
                      <Input
                        id="career-portfolio"
                        name="portfolio"
                        type="url"
                        placeholder="https://seu-portfolio.com"
                        value={formData.portfolio}
                        onChange={handleChange}
                        className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="career-message" className="text-white font-hero">Mensagem</Label>
                      <Textarea
                        id="career-message"
                        name="message"
                        placeholder="Conte-nos por que você seria perfeito para esta vaga..."
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="bg-dark-primary border-purple-primary/30 text-white placeholder:text-gray-500 resize-none"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isLoading}
                      className="w-full bg-purple-primary hover:bg-purple-light text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        'Enviar Candidatura'
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Briefcase className="mx-auto mb-4 text-gray-600" size={48} />
                    <p className="font-hero">Selecione uma vaga para se candidatar</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
